/**
 * Text Filter Input Component
 * Reusable text input for session filters
 * Matches Flutter's FloatTextFormField pattern
 */

import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { X } from 'lucide-react';
import { useCallback } from 'react';

interface TextFilterInputProps {
  label: string;
  value: string | number | undefined;
  onChange: (value: string | number | undefined) => void;
  onSubmit?: () => void;
  placeholder?: string;
  type?: 'text' | 'number';
  className?: string;
}

export function TextFilterInput({
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
  type = 'text',
  className = '',
}: TextFilterInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (type === 'number') {
        const parsed = newValue === '' ? undefined : parseInt(newValue, 10);
        onChange(isNaN(parsed as number) ? undefined : parsed);
      } else {
        onChange(newValue || undefined);
      }
    },
    [onChange, type]
  );

  const handleClear = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSubmit) {
        onSubmit();
      }
    },
    [onSubmit]
  );

  const displayValue = value !== undefined ? String(value) : '';
  const hasValue = displayValue !== '';

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <Input
          type={type}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || label}
          className="h-8 text-sm pr-8 placeholder:text-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
